/**
 * @fileoverview ページ下部に表示される資料の広告バナー用コンポーネント
 */
<script lang="ts">
import { defineComponent, ref, computed, watch, onMounted } from 'vue'
import FooterBannerCore from '@/components/banner/FooterBannerCore.vue'
import dateUtil from '@/utilities/date'
import { pathWithQueryParams } from '@/utilities/path'
import { useClientStore } from '@/stores/client'
import { useUserStore } from '@/stores/user'

export default defineComponent({
  name: 'FooterAdDocumentBanner',

  components: {
    FooterBannerCore,
  },

  setup() {
    // store
    const clientStore = useClientStore()
    const userStore = useUserStore()

    interface Banner {
      title?: string,
      caption?: string,
      description?: string,
      ctaLabel?: string,
      image: string,
      to: string,
      params: () => QueryParameter[],
      displayPeriod: {
        from: string,
        to: string,
      },
    }
    const BANNERS: Array<Banner> = [
      {
        title: '「これはアウト」の具体例と「する人・される人」の特徴とは？',
        caption: '無料資料',
        description: 'ハラスメントへの対策は、従業員を守るだけでなく、企業として存続するうえでも非常に重要です。本冊子では、パワハラする人・される人の傾向と、企業・個人ができる予防・対策を解説します。',
        ctaLabel: '無料ダウンロード',
        image: 'SmartHR_1028805.png',
        to: 'https://bizhint.jp/document/1028805',
        params: () => [clientStore.trackingCodeParam('sticky')],
        displayPeriod: {
          from: '2026-06-25T00:00:00+09:00',
          to: '2026-07-07T23:59:59+09:00',
        },
      },
    ]

    const imageLoaders = import.meta.glob('@/images/banner/footerAd/*', {
      query: '?url',
      import: 'default',
    })

    // 表示用バナー（設定されてるバナー配列からランダムで一つ表示される。）
    const renderBanner = ref<Banner | null>(null)

    const imageUrl = ref<string>()

    const isShow = ref(false)

    const bannerPath = computed(() => {
      if (!renderBanner.value) return ''

      return pathWithQueryParams(renderBanner.value.to, renderBanner.value.params())
    })

    /**
     * 実際にレンダリングするバナーが決定してから画像を非同期取得し、取得完了後にログイン済みだったら表示する
     */
    watch(() => renderBanner.value?.image, async (fileName) => {
      if (!fileName) return
      const loader = imageLoaders[`/images/banner/footerAd/${fileName}`]
      if (loader) {
        imageUrl.value = await loader() as string
        isShow.value = userStore.context.isSignedIn
      }
    }, { immediate: true })

    onMounted(() => {
      /**
       * 「表示期間」に開始と終了日時が正しく設定されており、
       * 且つ現在日時が期間内であるものを抽出
       */
      const activeBanners = BANNERS.filter((banner) => {
        const from = dateUtil.parseISO(banner.displayPeriod.from ?? '')
        const to = dateUtil.parseISO(banner.displayPeriod.to ?? '')
        return (
          dateUtil.isValid(from) &&
          dateUtil.isValid(to) &&
          dateUtil.isPast(from) &&
          !dateUtil.isPast(to)
        )
      })
      const renderIndex = Math.floor(Math.random() * activeBanners.length)
      renderBanner.value = activeBanners.length ? activeBanners[renderIndex] : null
    })

    return {
      // refs
      renderBanner,
      bannerPath,
      imageUrl,
      isShow,
    }
  },
})
</script>

<template>
  <FooterBannerCore
    :caption="renderBanner?.caption || ''"
    :description="renderBanner?.description || ''"
    :cta-label="renderBanner?.ctaLabel || ''"
    :image="imageUrl || ''"
    :to="bannerPath"
    :is-show="isShow"
    @close="isShow = $event">
    <template
      v-if="renderBanner?.title"
      #title>
      <div class="o-heading__footerAdBanner">
        <p class="o-heading__footerAdBanner--item">
          {{ renderBanner?.title || '' }}
        </p>
      <!-- /.o-heading__footerAdBanner --></div>
    </template>
  </FooterBannerCore>
</template>

<style lang="sass" scoped>
// object
.o-heading__footerAdBanner
  $root: &

  & &--item
    font-weight: bold

  +breakpoint
    #{$root}--item
      font-size: $font-small
</style>
